import React, {useCallback, useEffect, useRef, useState} from "react";
import Countdown from 'react-countdown';
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import {styled} from "@mui/material/styles";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import {
    getQuestionnaire,
    startQuestionnaire,
    completeQuestionnaire,
    getQuestionnaireInstance,
    getQuestionnaireReadonlyInstance,
    setQuestionnaireTemplate,
    setDataDefObj,
    currentPage,
    nextPage,
    prevPage,
    getNewTokenId,
    clearPortalPreviewMode
} from "./actions/portalActions";
import Content from "./Content";
import ErrorPageHook from "./common/ErrorPageHook";
import PortalPageRenderer from "./PortalPageRendererHook";
import {isRunningInInternetExplorer} from "./utilities/misc";
import * as ddoUtil from "./utilities/createPortalJsonObj";
import * as tag from "./constants/customMetadataConstants";
import * as ddo from "./constants/customObjConstants";
import * as color from "../constants/colorConstants";
import * as portalShared from "./shared/metadataConstantsAndUtilities"
import {imageDataUri} from "../utilities/imageCompression";
import * as system from "./constants/systemConstants";


// const theme = createTheme({
//     palette: {
//         primary: {
//             main: "#01579B"
//         },
//     }
// });

const PortalContainer = (props) => {
    const {
        portalPreview,
        questionnaireTemplateGraph,
        questionnaireTemplate,
        questionnaireInstance,
        questionnaireInstanceId,
        metadata,
        currPageIndex,
        message,
        answerQuestionOptions,
        answerQuestionTemplateOptions,
        answerQuestionText,
        answerQuestionTemplateText,
        templateType,
        portalCount,
        parent,
        startQuestionnaire,
        completeQuestionnaire,
        getQuestionnaire,
        getQuestionnaireInstance,
        getQuestionnaireReadonlyInstance,
        getNewTokenId,
        setQuestionnaireTemplate,
        setDataDefObj,
        currentPage,
        nextPage,
        prevPage,
        clearPortalPreviewMode
    } = props;

    const [state, setState] = useState({
        ddo: null, //Display Definition Object
        getQuestionnaireSent: false,
        currPageObjIndex: 0,
        canceled: false,
        displayMode: portalShared.INSTANT_MODE,
        firstQuestionMode: portalShared.SHOW_FIRST_QUESTION,
        surveyEditMode: portalShared.EDITABLE_SURVEY,
        surveyReadonlyHeaderMode: portalShared.READONLY_SURVEY_URL_SHOW_HEADER_NONE,
        switchOn: false,
        pageTitle: "",
        clearPage: false,
        showTokenExpirationModal: false,
        tokenValidationExpired: false,
        refKey: "",
        parsedJwtExp: 3540000,
        initComponent: true,
        portalCount: 0,
        startEventSent: false
    });

    const prevPropsRef = useRef();
    const tokenExpirationTimeoutRef = useRef(null);

    const setPortalState = useCallback((updates) => {
        setState((prevState) => ({...prevState, ...updates}));
    }, []);

    const clearTokenExpirationTimeout = useCallback(() => {
        if (tokenExpirationTimeoutRef.current) {
            clearTimeout(tokenExpirationTimeoutRef.current);
            tokenExpirationTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {

        return () => {
            clearTokenExpirationTimeout();
            clearPortalPreviewMode(); // Clear portal preview redux store props
        };
    }, [clearTokenExpirationTimeout, clearPortalPreviewMode]);

    const setPageTabTitle = useCallback((data = {}) => {
        data.title = data.title || 'QD Portal Questionnaire';
        document.title = data.title;
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) favicon.href = "/argoBlue.png";
    }, []);

    const portalMain = useCallback(() => {
        setPageTabTitle({title: state.pageTitle});

        let currURL =  new URL(window.location.href);

        let parsedURL = currURL.pathname.split("/");
        let refKey = parsedURL[2];

        //let refKey = this.props.match.params.key;

        // -------------------------------------------------------------------------------------------------------------
        // Get and PARSE the JWT (JSON Web Token) Security Token to get the timeout amount (JWT Security Token)
        let parsedJWT = state.parsedJwtExp;

        let token = localStorage.getItem(`${system.STORAGE_TOKEN}_${refKey}`); // getItem(STORAGE_TOKEN);

        if (token !== null) {

            let base64Url = token.split('.')[1];
            let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            let jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            parsedJWT = JSON.parse(jsonPayload); // 1659472470
        }

        let parsedJwtExp = (parsedJWT.exp === undefined) ? state.parsedJwtExp : parsedJWT.exp;

        // -------------------------------------------------------------------------------------------------------------

        setPortalState({refKey: refKey, parsedJwtExp: parsedJwtExp});

        if (refKey !== undefined) {

            // If a ref key then a query string can answer the first question key?firstQuestionIndex=2
            if (token === "") {
                getNewTokenId(refKey);
            }

            // show warning message modal for expiring token
            clearTokenExpirationTimeout();
            tokenExpirationTimeoutRef.current = setTimeout(() => {
                setPortalState({showTokenExpirationModal: true});
            }, parsedJwtExp);  // default was 3540000

            let urlParams = currURL.search;
            let query = new URLSearchParams(currURL.search); //need new ways to get ?readonly=true and other query options

            let answerIndex = null;
            let surveyEditMode = portalShared.EDITABLE_SURVEY;
            let surveyReadonlyHeaderMode = portalShared.READONLY_SURVEY_URL_SHOW_HEADER_NONE;

            // NOTE: there are 2 types of refKeys, the instance and the readonly instance, if readonly parameter is used you must use the readonly refKey
            // example URL http://localhost:3001/qd2portal/ExIB5E2kqqz95ASxp5mT?readonly=true for readonly mode
            // .get is a function in the new URLSearchParams
            if ((query !== "") && (query.get("readonly") === "true")) {
                // &header=false|filtered parameter NOTE: the questionnaire can use metadata for this HIDE_READONLY_HEADER or FILTERED_READONLY_HEADER
                surveyEditMode = portalShared.READONLY_SURVEY;
                if (query.get("header") === "false") {
                    surveyReadonlyHeaderMode = portalShared.READONLY_SURVEY_URL_SHOW_HEADER_FALSE;
                }

                // NOTE: show header is by default is filtered, but this will OVERRIDE questionnaire hide or show metadata tag.
                if (query.get("header") === "filter") {
                    surveyReadonlyHeaderMode = portalShared.READONLY_SURVEY_URL_SHOW_HEADER_FILTERED;
                }

                if (query.get("header") === "true") {
                    surveyReadonlyHeaderMode = portalShared.READONLY_SURVEY_URL_SHOW_HEADER_TRUE;
                }
            }
            else {

                // I think the way this works is ?answer=###  where ### is the ID of the first answer on the survey
                answerIndex = urlParams.match(/\d+/);
                if (answerIndex) {
                    answerIndex = answerIndex[0];
                } else{
                    answerIndex = null;
                }
            }

            let firstQuestionMode = (answerIndex === null) ? portalShared.SHOW_FIRST_QUESTION : portalShared.SKIP_FIRST_QUESTION;

            // If the first question is answered then start questionnaire will be triggered with get instance and first question answered
            setPortalState({firstQuestionMode: firstQuestionMode, displayMode: portalShared.INSTANT_MODE, surveyEditMode: surveyEditMode, surveyReadonlyHeaderMode: surveyReadonlyHeaderMode});

            // NOTE: there are 2 types of refKeys, the instance and the readonly instance, if readonly parameter is used you must use the readonly refKey
            if (surveyEditMode === portalShared.READONLY_SURVEY) {
                getQuestionnaireReadonlyInstance(refKey, answerIndex); // Get the instance ID
            }
            else {
                getQuestionnaireInstance(refKey, answerIndex); // Get the instance ID
            }

        }

    }, [clearTokenExpirationTimeout, getNewTokenId, getQuestionnaireInstance, getQuestionnaireReadonlyInstance, setPageTabTitle, setPortalState, state.pageTitle, state.parsedJwtExp]);

    const portalPreviewMain = useCallback(() => {
        let clonedTemplateObj = null;
        let ddo = null;

        if (state.portalCount !== portalCount) {
            setQuestionnaireTemplate(questionnaireTemplateGraph);
            //props.setTemplateObj(props.templateObj);
            currentPage(0);

            clonedTemplateObj = portalShared.cloneObj(questionnaireTemplateGraph);
            ddo = ddoUtil.createJsonObjFromQuestionnaire(clonedTemplateObj, portalShared.TEMPLATE_MODE, portalShared.SHOW_FIRST_QUESTION, portalShared.EDITABLE_SURVEY);

            // Convert questionnaire template graph to a survey ddo (Display Definition Object)
            //ddo = ddoUtil.createJsonObjFromQuestionnaire(clonedTemplateObj, portalShared.TEMPLATE_MODE, portalShared.SHOW_FIRST_QUESTION, portalShared.EDITABLE_SURVEY);

            setDataDefObj(ddo); // Save DataDefObj in REDUX store of required fields and nested show groups

            setPortalState({initComponent: false, portalCount: portalCount, ddo: ddo, displayMode: portalShared.TEMPLATE_MODE});
        }

    }, [currentPage, portalCount, questionnaireTemplateGraph, setPortalState, setQuestionnaireTemplate, setDataDefObj, state.portalCount]);

    const initComponent = useCallback(() => {
        setPortalState({initComponent: false});

        if (portalPreview) {
            portalPreviewMain();
        } else {
            portalMain();
        }
    }, [portalMain, portalPreview, portalPreviewMain, setPortalState]);

    useEffect(() => {

        if (state.initComponent) {
            initComponent();
        }
    }, [initComponent, state.initComponent]);

    useEffect(() => {

        const prevProps = prevPropsRef.current || {};
        const nextProps = {
            questionnaireTemplateGraph,
            questionnaireTemplate,
            portalPreview,
            questionnaireInstanceId,
            questionnaireInstance,
            metadata
        };

        if (nextProps.questionnaireTemplateGraph) {
            let templateGraphObjFromAdminString = JSON.stringify(nextProps.questionnaireTemplateGraph);
            let templateObjFromStoreString = JSON.stringify(nextProps.questionnaireTemplate); // Saved graph obj from admin in REDUX store

            // When a questionnaire is created for portal preview it will be set into the redux store
            if (nextProps.portalPreview && ((templateGraphObjFromAdminString !== templateObjFromStoreString) || (state.ddo === null))) {

                // Portal Preview mode and the templateObj has changed from the one in the redux store
                portalPreviewMain();
            }
        }
        else if (state.ddo === null) {
            if ((nextProps.questionnaireInstanceId !== null) && (!state.getQuestionnaireSent)) {
                getQuestionnaire(nextProps.questionnaireInstanceId);  // Get the full instance
                setPortalState({getQuestionnaireSent: true});
            }
            else if (nextProps.questionnaireInstance !== null && nextProps.questionnaireInstance !== prevProps.questionnaireInstance) {

                // ########################################
                // CREATE SURVEY JSON OBJECT FROM INSTANCE
                // ########################################

                let ddo = ddoUtil.createJsonObjFromQuestionnaire(nextProps.questionnaireInstance, portalShared.INSTANT_MODE, state.firstQuestionMode, state.surveyEditMode, state.surveyReadonlyHeaderMode, nextProps.metadata);

                // By default, if NO Portal Tab Title is given then use Questionnaire Name
                let portalTabTitle = nextProps.questionnaireInstance.name;
                if (tag.metadataExists(nextProps.questionnaireInstance, portalShared.PORTAL_TAB_TITLE)) {
                    portalTabTitle = tag.metadataValue(nextProps.questionnaireInstance, portalShared.PORTAL_TAB_TITLE);
                }
                setPageTabTitle({title: portalTabTitle});

                setDataDefObj(ddo); // Save DataDefObj in REDUX store of required fields and nested show groups

                setPortalState({ddo: ddo});  // Set the Display Definition Object
            }
            else if (nextProps.questionnaireTemplate !== null && nextProps.questionnaireTemplate !== prevProps.questionnaireTemplate) {

                // ########################################
                // CREATE SURVEY JSON OBJECT FROM TEMPLATE
                // ########################################

                // If questionnaire portal preview mode or admin override then wait for template from server to build ddo (Display Definition Object)
                let ddo = ddoUtil.createJsonObjFromQuestionnaire(nextProps.questionnaireTemplate, portalShared.TEMPLATE_MODE, portalShared.SHOW_FIRST_QUESTION, portalShared.EDITABLE_SURVEY);

                setDataDefObj(ddo); // Save DataDefObj in REDUX store of required fields and nested show groups

                setPortalState({ddo: ddo});  // Set the Display Definition Object
            }
        }

        prevPropsRef.current = nextProps;
    }, [getQuestionnaire, portalPreview, portalPreviewMain, questionnaireInstance, questionnaireInstanceId, questionnaireTemplate, questionnaireTemplateGraph, metadata, setPageTabTitle, setPortalState, state.ddo, state.firstQuestionMode, state.getQuestionnaireSent, state.surveyEditMode, state.surveyReadonlyHeaderMode, setDataDefObj]);

    const startSurvey = useCallback(() => {
        let isInstance = (questionnaireInstance !== null);
        let qnr =  isInstance ? questionnaireInstance : questionnaireTemplate;

        if (isInstance && qnr.started) {
            startQuestionnaire(qnr.id);
        }
        nextPage(currPageIndex);
    }, [currPageIndex, nextPage, questionnaireInstance, questionnaireTemplate, startQuestionnaire]);

    const cancelSurvey = useCallback(() => {
        setPortalState({canceled: true});
    }, [setPortalState]);

    const finishSurvey = useCallback(() => {
        let isInstance = (questionnaireInstance !== null);
        let qnr =  isInstance ? questionnaireInstance : questionnaireTemplate;

        if (isInstance) {
            completeQuestionnaire(qnr.id);
        }

        // Check the last category defined, IF it is a new-page "close" then after submit advance to close page
        let categories = isInstance ? qnr.questionCategories : qnr.questionCategoryTemplates;
        let i = categories.length-1;

        if (tag.metadataExistsObj(qnr, portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ) || tag.metadataExistsObj(qnr, portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ_LEGACY)) {
            nextPage(currPageIndex);
        }
        else if (tag.metadataExistsInObj(categories[i], portalShared.CUSTOM_QNR_START_PAGE_OBJ, "portalPageBreak", true)) {
            //if (tag.metadataValue(categories[i], portalShared.CATEGORY_NEW_PAGE) === 'close') {
            nextPage(currPageIndex);
            //}
        }
        else {
            setPortalState({clearPage: true});
        }
    }, [completeQuestionnaire, currPageIndex, nextPage, questionnaireInstance, questionnaireTemplate, setPortalState]);

    const nextPageHandler = useCallback(() => {
        nextPage(currPageIndex);
    }, [currPageIndex, nextPage]);

    const prevPageHandler = useCallback(() => {
        prevPage(currPageIndex);
    }, [currPageIndex, prevPage]);

    const onlyCloseExpirationModal = useCallback(() => {
        setPortalState({showTokenExpirationModal: false});
    }, [setPortalState]);

    const closeExpirationModalAndGetNewToken = useCallback(() => {
        getNewTokenId(state.refKey);

        setPortalState({
            showTokenExpirationModal: false,
            tokenValidationExpired: false
        });

        clearTokenExpirationTimeout();
        tokenExpirationTimeoutRef.current = setTimeout(() => {
            setPortalState({showTokenExpirationModal: true});
        }, state.parsedJwtExp); // 3540000
    }, [clearTokenExpirationTimeout, getNewTokenId, setPortalState, state.parsedJwtExp, state.refKey]);

    const disableTokenExpirationModalOkButton = useCallback(() => {
        setPortalState({tokenValidationExpired: true});
    }, [setPortalState]);

    // const getAppHeaderTitleLegacyObj = useCallback((qnr, position, type, titleObj, metadataImageTag) => {
    //     // ZZZZZ
    //     let returnObj = {
    //         showImage: false,
    //         text: "",
    //         variant: "h2",
    //         image: "",
    //         format: "default",
    //         textObj: null,
    //         CustomTypography: null
    //     };
    //
    //     // Images are stored separate in metadata because we use Base64 encryption
    //     if (type === "text") {
    //         returnObj.text   = titleObj.title;
    //         returnObj.textObj   = titleObj;
    //         returnObj.format   = titleObj.format;
    //
    //         if (titleObj.format === "custom") {
    //             returnObj.CustomTypography = styled(Typography)({
    //                 fontFamily: titleObj.fontFamily,
    //                 fontWeight: titleObj.fontWeight,
    //                 color: titleObj.colorObj.colorType === "default" ? "white" : titleObj.colorObj.color,
    //                 fontSize: titleObj.fontSize,  // 2em, 18pt
    //                 fontStyle: titleObj.fontStyle,
    //                 textAlign: position,  // left center right
    //                 opacity: titleObj.opacity
    //             });
    //         }
    //     }
    //     else if (type === "image") {
    //         returnObj.showImage   = tag.metadataExists(qnr, metadataImageTag);
    //         if (returnObj.showImage) {
    //             returnObj.image   = tag.metadataValue(qnr, metadataImageTag);
    //         }
    //     }
    //
    //     return returnObj;
    // }, []);

    const getAppHeaderTitleObj = useCallback((qnr, position, type, titleObj, metadataImageTag, currPage=0, numPages=0) => {
        let returnObj = {
            showImage: false,
            text: "",
            variant: "h2",
            image: "",
            format: "default",
            textObj: null,
            CustomTypography: null
        };

        let surveyHasStartPage = qnr.metadata.metadataItems.hasOwnProperty(portalShared.CUSTOM_QNR_START_PAGE_OBJ);
        let tempCurrPage = currPage + (surveyHasStartPage ? 0 : 1);

        // Images are stored separate in metadata because we use Base64 encryption
        if (type === "text" || type === "pageNum" || type === "pageofNum") {

            let title = titleObj.title;

            if (type === "pageNum") {
                title = "";
                if (tempCurrPage > 0 && tempCurrPage <= numPages) {
                    title = "Page " + tempCurrPage;
                }
            }

            if (type === "pageofNum") {
                title = "";
                if (tempCurrPage > 0 && tempCurrPage <= numPages) {
                    title = "Page " + tempCurrPage + " of " + numPages;
                }
            }

            returnObj.text   = title;
            returnObj.textObj   = titleObj;
            returnObj.format   = titleObj.format;

            if (titleObj.format === "custom") {
                returnObj.CustomTypography = styled(Typography)({
                    fontFamily: titleObj.fontFamily,
                    fontWeight: titleObj.fontWeight,
                    color: portalShared.getColor(titleObj.colorObj), // titleObj.colorObj.colorType === "default" ? "white" : titleObj.colorObj.color,
                    fontSize: titleObj.fontSize,  // 2em, 18pt
                    fontStyle: titleObj.fontStyle,
                    textAlign: position,  // left center right
                    opacity: titleObj.opacity
                });
            }
        }
        else if (type === "image") {
            returnObj.showImage   = tag.metadataExists(qnr, metadataImageTag);
            if (returnObj.showImage) {
                returnObj.image   = tag.metadataValue(qnr, metadataImageTag);
            }
        }

        return returnObj;
    }, []);

    const getAppHeaderTitle = useCallback((titleObj, width, textAlign) => {
        return(<div style={{width: width, display: "flex", flexDirection: "row", justifyContent: textAlign, alignItems: "center"}}>
                {titleObj.showImage ?
                    <img src={imageDataUri(titleObj.image)} alt="" style={{verticalAlign: "middle",height: "60px"}}/>
                    :
                    (titleObj.format === "custom") ?
                        <titleObj.CustomTypography>{titleObj.text}</titleObj.CustomTypography>
                        :
                        <Typography variant={titleObj.variant} color="inherit">
                            {titleObj.text}
                        </Typography>
                }
            </div>
        );
    }, []);

    const closeSurvey = undefined;

    let errorMessage = message;

    const MainComponent = isRunningInInternetExplorer() ? `div` : `main`;

    let currPageDefObj = null;
    let totalNumberOfPages = state.ddo?.totalPages;

    //let customColor = "";

    // let uploadedImageStyles = {
    //     verticalAlign: 'middle',
    //     height: '60px'
    // };

    // Check to make sure the ddo (Display Definition Object) has been added to the local state
    if (state.ddo !== null && currPageIndex !== undefined) {
        currPageDefObj = state.ddo[ddo.OBJ.KEY.DDO.PAGE_ARRAY][currPageIndex];
        //totalNumberOfPages = state.ddo[ddo.OBJ.KEY.DDO.PAGE_ARRAY].length;
    }

    let isInstance = (questionnaireInstance !== null);
    let qnr =  isInstance ? questionnaireInstance : questionnaireTemplate;

    //Commenting out to allow ErrorPage to work. purpose of return unknown
    // if (qnr === null) {
    //     return (<div></div>); // Questionnaire NOT yet loaded
    // }

    let showAppBar = (tag.metadataExists(qnr , portalShared.CUSTOM_QNR_APP_HEADER_OBJ, "", true) || tag.metadataExists(qnr , portalShared.CUSTOM_QNR_APP_HEADER_OBJ_LEGACY, "", true));
    let appBarColor =  color.COLOR_PRIMARY_BLUE_HEX;
    let leftTitleObj = null;
    let centerTitleObj = null;
    let rightTitleObj = null;

    // Portal preview does not show appBar
    if (showAppBar && !portalPreview) {

        if (tag.metadataExists(qnr , portalShared.CUSTOM_QNR_APP_HEADER_OBJ_LEGACY, "", true)) {
            let appHeaderLegacyObj = JSON.parse(tag.metadataValue(qnr , portalShared.CUSTOM_QNR_APP_HEADER_OBJ_LEGACY));
            let appHeaderObj = portalShared.getCustomQuestionnaireAppHeaderObj();

            appHeaderObj.appHeaderLeftType = "image";
            appHeaderObj.appHeaderCenterType = "none";
            appHeaderObj.appHeaderRightType = "text";

            appHeaderObj.appHeaderRightTextObj = portalShared.cloneObj(portalShared.customTextObject);
            appHeaderObj.appHeaderRightTextObj.title = appHeaderLegacyObj.textRight;
            appHeaderObj.appHeaderRightTextObj.format = "custom";
            appHeaderObj.appHeaderRightTextObj.hide = false;
            appHeaderObj.appHeaderRightTextObj.objType = "text";
            appHeaderObj.appHeaderRightTextObj.fontSizing = "points";
            appHeaderObj.appHeaderRightTextObj.fontSizeEntry = "14";
            appHeaderObj.appHeaderRightTextObj.fontSize = "14pt";
            appHeaderObj.appHeaderRightTextObj.variant = "h3";
            appHeaderObj.appHeaderRightTextObj.fontStyle = "normal";
            appHeaderObj.appHeaderRightTextObj.textAlign = "left";
            appHeaderObj.appHeaderRightTextObj.colorObj.color = "#000000";

            leftTitleObj = getAppHeaderTitleObj(qnr, "left", appHeaderObj.appHeaderLeftType, appHeaderObj.appHeaderLeftTextObj, portalShared.HEADER_APPBAR_IMAGE_LEFT);
            centerTitleObj = getAppHeaderTitleObj(qnr, "center", appHeaderObj.appHeaderCenterType, appHeaderObj.appHeaderCenterTextObj, portalShared.HEADER_APPBAR_IMAGE_CENTER);
            rightTitleObj = getAppHeaderTitleObj(qnr, "right", appHeaderObj.appHeaderRightType, appHeaderObj.appHeaderRightTextObj, portalShared.HEADER_APPBAR_IMAGE_RIGHT);
        }
        else {
            // Has to be an instance so use metadata.metadataItems property
            let appHeaderObj = tag.metadataValueObj(qnr, portalShared.CUSTOM_QNR_APP_HEADER_OBJ);

            if (appHeaderObj.colorObj !== null) {
                appBarColor = appHeaderObj.colorObj.colorEntry;
            }

            //zzzzz2

            leftTitleObj = getAppHeaderTitleObj(qnr, "left", appHeaderObj.appHeaderLeftType, appHeaderObj.appHeaderLeftTextObj, portalShared.HEADER_APPBAR_IMAGE_LEFT, currPageIndex, totalNumberOfPages);
            centerTitleObj = getAppHeaderTitleObj(qnr, "center", appHeaderObj.appHeaderCenterType, appHeaderObj.appHeaderCenterTextObj, portalShared.HEADER_APPBAR_IMAGE_CENTER, currPageIndex, totalNumberOfPages);
            rightTitleObj = getAppHeaderTitleObj(qnr, "right", appHeaderObj.appHeaderRightType, appHeaderObj.appHeaderRightTextObj, portalShared.HEADER_APPBAR_IMAGE_RIGHT, currPageIndex, totalNumberOfPages);
        }
    }

    // If no start page then START event is sent when survey loaded
    useEffect(() => {

        if (!tag.metadataExists(qnr, portalShared.CUSTOM_QNR_START_PAGE_OBJ)) {
            if (qnr) {
                if ((isInstance) && ((qnr.started !== undefined) && !qnr.started && !state.startEventSent)) {
                    setPortalState({startEventSent: true});
                    startQuestionnaire(qnr.id);
                }
            }
        }
    }, [isInstance, qnr, setPortalState, startQuestionnaire, state.startEventSent]);

    let mainPadding = "20px 0px 20px 0px";
    if (!portalPreview && showAppBar)  {
        // Portal Preview will NEVER have an AppBar
        mainPadding = "80px 0px 20px 0px"; // If there is an AppBar then adjust padding to push survey below AppBar
    }

    return (
        (questionnaireInstance || questionnaireTemplate || errorMessage !== "") &&

        <div style={{height: '100%', width: "100%", display: "flex", flexDirection: "column"}}>
            {(!portalPreview && showAppBar)  ?

                    <AppBar position="fixed" component="div" style={{backgroundColor: appBarColor}} elevation={0}>
                        <Toolbar>
                            <div style={{display: "inline-flex", width: "100%"}}>
                                {getAppHeaderTitle(leftTitleObj, "30%", "left")}
                                {getAppHeaderTitle(centerTitleObj, "40%", "center")}
                                {getAppHeaderTitle(rightTitleObj, "30%", "right")}
                            </div>
                        </Toolbar>
                    </AppBar> : ""
            }

            <MainComponent style={{backgroundColor: '#FAFAFA', display: "flex", overflowY: "auto", overflowX: "hidden", alignItems: "center", padding: mainPadding}}>
                {state.clearPage ? <div></div> :
                    <Content>

                        {(currPageDefObj !== null) ?
                            <PortalPageRenderer pageDefObj={currPageDefObj}
                                                pageDefObjCurrIndex={currPageIndex}
                                                totalPages={totalNumberOfPages}
                                                questionnaireInstance={questionnaireInstance}
                                                questionnaireTemplate={questionnaireTemplate}
                                                style={{width: '1000px'}}
                                                start={startSurvey}
                                                cancel={cancelSurvey}
                                                finish={finishSurvey}
                                                next={nextPageHandler}
                                                previous={prevPageHandler}
                                                close={closeSurvey}
                                                answerQuestionOptions={answerQuestionOptions}
                                                answerQuestionTemplateOptions={answerQuestionTemplateOptions}
                                                answerQuestionText={answerQuestionText}
                                                answerQuestionTemplateText={answerQuestionTemplateText}
                                                displayMode={state.displayMode}
                                                surveyEditMode={state.surveyEditMode}
                                                templateType={templateType}
                                                portalPreview={portalPreview}
                                                portalCount={portalCount}
                                                tabIndex={(parent === undefined || parent === "QuestionnairePortalPreviewDrawer") ? 0 : -1}
                            />
                            :
                            <ErrorPageHook/>
                        }
                    </Content>
                }
            </MainComponent>
            {
                state.showTokenExpirationModal ?
                    <div style={{position: 'absolute'}}>
                        <Modal
                            aria-labelledby="simple-modal-title"
                            aria-describedby="simple-modal-description"
                            open={state.showTokenExpirationModal}
                            onClose={onlyCloseExpirationModal}
                            style={{display: "flex",alignItems: "center",justifyContent: "center"}}
                        >
                            <div style={{backgroundColor: 'white', width: '500px', height: '200px', padding: '10px'}}>
                                {
                                    state.tokenValidationExpired ?
                                        <div>
                                            <Typography>Session Expired</Typography>
                                            <Typography>Reload page to continue</Typography>
                                        </div> :
                                        <div>
                                            <Typography variant="h6" id="modal-title" style={{color: "red", marginBottom: '10px'}}>
                                                Warning: Your session is about to expire
                                            </Typography>
                                            <Typography variant="subtitle1" id="simple-modal-description" style={{marginBottom: '20px'}}>
                                                Click "OK" to continue your session, otherwise your session will expire in:
                                            </Typography>
                                            <Countdown date={Date.now() + 60000} onComplete={disableTokenExpirationModalOkButton}/>
                                            <div style={{display: "flex", justifyContent: "center"}}>
                                                <Button disabled={state.tokenValidationExpired} style={{border: 'solid', borderColor: 'black', borderWidth: '2px', marginTop: '30px'}}
                                                        onClick={closeExpirationModalAndGetNewToken}
                                                >
                                                    OK
                                                </Button>
                                            </div>
                                        </div>
                                }
                            </div>
                        </Modal>
                    </div>
                    : null
            }
        </div>
    );
};


PortalContainer.defaultProps = {
    portalPreview: false,
    templateType: "",
    templateId: null,
    templateObj: null,
    questionnaireInstanceId: null,
    questionnaireInstance: null,
    questionnaireTemplateId: null,
    questionnaireTemplate: null
};


PortalContainer.propTypes = {
    portalPreview: PropTypes.bool,
    templateType: PropTypes.string,
    templateId: PropTypes.number,
    templateObj: PropTypes.object,
    questionnaireTemplateGraph: PropTypes.object,
    portalCount: PropTypes.number,
};


function mapStateToProps(state) {
    return {
        theme: state.portalTheme,
        refKey: state.portal.refKey,
        metadata: state.portal.metadata,
        questionnaireInstanceId: state.portal.questionnaireInstanceId,
        questionnaireInstance: state.portal.questionnaireInstance,
        questionnaireTemplateId: state.portal.questionnaireTemplateId,
        questionnaireTemplate: state.portal.questionnaireTemplate,
        currPageIndex: state.portal.currPageIndex,
        portal: state.portal,
        message: state.errorResponse.message,
    };
}


function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        dispatch,
        startQuestionnaire,
        completeQuestionnaire,
        getQuestionnaire,
        getQuestionnaireInstance,
        getNewTokenId,
        getQuestionnaireReadonlyInstance,
        setQuestionnaireTemplate,
        setDataDefObj,
        currentPage,
        nextPage,
        prevPage,
        clearPortalPreviewMode
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PortalContainer);
